import type { SortChangedEvent } from "@performyard/py-components";
import type { CompetencyAssignmentJSON, Level } from "@performyard/service-contracts";

import type { TeamCompetencyAssignmentSortBy } from "../../api/services/defs/competency-assignments";

// A stored rank never renders blank. Archived rows keep an expected_level that a later level-list
// shrink removed, and current_level is never cleared at all, so the name can be gone while the value
// is real. null is reserved for a genuinely absent rank — it's what empties the cell. Kept in step
// with the CSV export's resolveLevelName so the download matches what the grid showed.
export const resolveLevelName = (
	levels: Pick<Level, "rank" | "name">[] | undefined,
	rank: number | null | undefined
): string | null => {
	if (rank === null || rank === undefined) return null;
	return levels?.find((l) => l.rank === rank)?.name ?? `Level ${rank}`;
};

export const PAGE_SIZE = 25;

// Sort mapping for the shared CompetenciesTable when the Assignee column is present (team + company pages).
export const colIdToSortBy = (colId: string | undefined): TeamCompetencyAssignmentSortBy | null => {
	if (
		colId === "assignee_name" ||
		colId === "competency_name" ||
		colId === "expected_level" ||
		colId === "current_level"
	)
		return colId;
	// "role_name" is intentionally absent — the Role column is not sortable (Pitstop-4 decision).
	return null;
};

export type CompetencySort = { name: string; direction: "asc" | "desc" };

// The competencies tables sort by a single column; pull it out of the grid's sort event.
export const competencySortFromEvent = (event: SortChangedEvent<CompetencyAssignmentJSON>): CompetencySort | null => {
	const sortedColumn = event.api.getColumnState().find((column) => column.sort);
	if (!sortedColumn?.colId || !sortedColumn.sort) return null;
	return { name: sortedColumn.colId, direction: sortedColumn.sort };
};
